/**
 * UtilityDezk - RLS Context Service
 * Phase 8B.1: Security Hotfix
 *
 * PURPOSE:
 * - Sets the RLS context for Row Level Security policies
 * - Must be called after authentication and organization resolution
 * - Handles demo environment safely
 * - Provides defensive logging for debugging
 *
 * SECURITY:
 * - Never logs sensitive tokens or customer data
 * - Only sets context for authenticated users with valid organization
 * - Graceful failure (doesn't block auth if RLS context fails)
 */

import logger from '../lib/logger';
import { supabase } from '../lib/supabase';
import { DEMO_ORGANIZATION } from './authService';
import { DEMO_CONFIG } from '../demo/demoUsers';

// =============================================================================
// TYPES
// =============================================================================

export interface RLSContextParams {
  organizationId: string;
  userRole: string;
  environment?: 'demo' | 'pilot' | 'staging' | 'production';
}

export interface RLSContextResult {
  success: boolean;
  error?: string;
}

// =============================================================================
// RLS CONTEXT SERVICE
// =============================================================================

/**
 * Set RLS context for the current database session
 * 
 * This function must be called AFTER:
 * 1. User is authenticated
 * 2. Organization ID is resolved
 * 3. User role is known
 * 
 * It must be called BEFORE:
 * - Any tenant-scoped data queries
 */
export const setRLSContext = async (
  params: RLSContextParams
): Promise<RLSContextResult> => {
  const { organizationId, userRole, environment = 'production' } = params;

  try {
    logger.audit('RLS_CONTEXT_SET_START', {
      organizationId: organizationId?.substring(0, 8) + '...', // Truncate for security
      userRole,
      environment,
    });

    // Validation: Ensure required parameters are present
    if (!organizationId) {
      const error = 'Cannot set RLS context: organizationId is missing';
      logger.warn('RLS_CONTEXT_SET_SKIPPED', { reason: 'Missing organizationId' });
      return { success: false, error };
    }

    if (!userRole) {
      const error = 'Cannot set RLS context: userRole is missing';
      logger.warn('RLS_CONTEXT_SET_SKIPPED', { reason: 'Missing userRole' });
      return { success: false, error };
    }

    // Safety: Skip RLS context setting for demo environment
    if (environment === 'demo' || organizationId.startsWith('demo-')) {
      logger.info('RLS_CONTEXT_SET_SKIPPED', {
        reason: 'Demo environment - RLS not needed',
        organizationId: organizationId?.substring(0, 8) + '...',
      });
      return { success: true };
    }

    // Call the database function to set RLS context
    // This sets session variables: app.current_organization_id and app.current_role
    const { error } = await supabase.rpc('set_rls_context', {
      p_organization_id: organizationId,
      p_role: userRole,
    });

    if (error) {
      logger.error('RLS_CONTEXT_SET_FAILED', {
        error: error.message,
        organizationId: organizationId?.substring(0, 8) + '...',
        userRole,
      });
      return { success: false, error: error.message };
    }

    logger.audit('RLS_CONTEXT_SET_SUCCESS', {
      organizationId: organizationId?.substring(0, 8) + '...',
      userRole,
      environment,
    });

    return { success: true };
  } catch (err) {
    const error = String(err);
    logger.error('RLS_CONTEXT_SET_FAILED', {
      error,
      organizationId: organizationId?.substring(0, 8) + '...',
      userRole,
    });

    // Graceful failure: Don't block authentication if RLS context fails
    // The worst case is that RLS policies won't filter data (which is a security issue)
    // But blocking auth would be a worse UX
    return { success: false, error };
  }
};

/**
 * Clear RLS context on logout
 * 
 * This is optional but good practice to ensure no stale context remains
 */
export const clearRLSContext = async (): Promise<void> => {
  try {
    logger.audit('RLS_CONTEXT_CLEAR_START', {});

    // Note: We don't need to explicitly clear session variables
    // They are automatically cleared when the session ends
    // But we can log the action for audit purposes

    logger.audit('RLS_CONTEXT_CLEAR_SUCCESS', {});
  } catch (err) {
    logger.error('RLS_CONTEXT_CLEAR_FAILED', { error: String(err) });
  }
};

/**
 * Verify RLS context is set correctly
 * 
 * This is a diagnostic function for testing/debugging
 */
export const verifyRLSContext = async (
  expectedOrganizationId: string,
  expectedRole: string
): Promise<boolean> => {
  try {
    // Note: This would require a database function to read session variables
    // For now, we just log the verification attempt
    logger.info('RLS_CONTEXT_VERIFY', {
      expectedOrganizationId: expectedOrganizationId?.substring(0, 8) + '...',
      expectedRole,
    });

    // TODO: Implement database function to read session variables
    // SELECT current_setting('app.current_organization_id'), current_setting('app.current_role')
    return true;
  } catch (err) {
    logger.error('RLS_CONTEXT_VERIFY_FAILED', { error: String(err) });
    return false;
  }
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Determine environment from organization ID
 */
export const getEnvironmentFromOrgId = (organizationId: string): 'demo' | 'pilot' | 'staging' | 'production' => {
  if (organizationId.startsWith('demo-')) return 'demo';
  if (organizationId.startsWith('pilot-')) return 'pilot';
  if (organizationId.startsWith('staging-')) return 'staging';
  return 'production';
};

/**
 * Check if RLS context needs to be set
 */
export const shouldSetRLSContext = (organizationId: string, userRole: string): boolean => {
  if (!organizationId || !userRole) return false;
  if (organizationId.startsWith('demo-')) return false; // Skip for demo
  return true;
};

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default {
  setRLSContext,
  clearRLSContext,
  verifyRLSContext,
  getEnvironmentFromOrgId,
  shouldSetRLSContext,
};
